import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { PictureAsPdf, VisibilityOutlined } from '@mui/icons-material';
import { recipeV2Client, RecipeV2, RecipeV2Detail } from '../../../app/services/api/recipeV2';
import { loadGovernedPublishingRecipes } from '../publishing/loadPublishingRecipes';
import {
  RecipePublishStyle,
  saveAllergenMatrixPdf,
  saveRecipeBookPdf,
} from '../publishing/recipePublishing';
import GovernedMatrixPreview from './GovernedMatrixPreview';
import CustomPagination from '../../../components/forms/Pagination';

interface GenerateRecipeBookDialogProps {
  open: boolean;
  onClose: () => void;
}

const BANK_PAGE_SIZE = 100;
const SELECTION_PAGE_SIZE = 8;

const loadAllBankRecipeIds = async (): Promise<number[]> => {
  const firstPage = await recipeV2Client.getRecipeBankList('', '', [], 1, BANK_PAGE_SIZE);
  const pageCount = Math.ceil(firstPage.totalCount / BANK_PAGE_SIZE);

  if (pageCount <= 1) {
    return firstPage.data.map((recipe) => recipe.id);
  }

  const remainingPages = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, index) =>
      recipeV2Client.getRecipeBankList('', '', [], index + 2, BANK_PAGE_SIZE)),
  );

  return [firstPage, ...remainingPages]
    .flatMap((response) => response.data)
    .map((recipe) => recipe.id);
};

const GenerateRecipeBookDialog = ({ open, onClose }: GenerateRecipeBookDialogProps) => {
  const { t } = useTranslation('recipeV2');
  const [recipes, setRecipes] = useState<RecipeV2[]>([]);
  const [selectionPage, setSelectionPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [allSelected, setAllSelected] = useState(true);
  // When allSelected is true this set contains exclusions; otherwise it contains inclusions.
  const [selectionOverrides, setSelectionOverrides] = useState<Set<number>>(new Set());
  const [style, setStyle] = useState<RecipePublishStyle>('print');
  const [output, setOutput] = useState<'book' | 'matrix'>('book');
  const [matrixPreview, setMatrixPreview] = useState<RecipeV2Detail[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setRecipes([]);
      setSelectionPage(0);
      setTotalCount(0);
      setAllSelected(true);
      setSelectionOverrides(new Set());
      setMatrixPreview([]);
      return;
    }

    let active = true;
    setIsLoading(true);
    setError(null);
    recipeV2Client.getRecipeBankList('', '', [], selectionPage + 1, SELECTION_PAGE_SIZE)
      .then((response) => {
        if (!active) return;
        setRecipes(response.data);
        setTotalCount(response.totalCount);
      })
      .catch((cause) => {
        if (!active) return;
        setRecipes([]);
        setError(cause instanceof Error ? cause.message : 'Failed to load recipes');
      })
      .finally(() => { if (active) setIsLoading(false); });

    return () => { active = false; };
  }, [open, selectionPage]);

  const selectedCount = allSelected
    ? Math.max(0, totalCount - selectionOverrides.size)
    : Math.min(totalCount, selectionOverrides.size);

  const selectionPageCount = Math.ceil(totalCount / SELECTION_PAGE_SIZE);

  const clearPreview = () => setMatrixPreview([]);

  const isRecipeSelected = (recipeId: number) =>
    allSelected ? !selectionOverrides.has(recipeId) : selectionOverrides.has(recipeId);

  const toggleRecipe = (recipeId: number) => {
    clearPreview();
    setSelectionOverrides((current) => {
      const next = new Set(current);
      if (next.has(recipeId)) next.delete(recipeId);
      else next.add(recipeId);
      return next;
    });
  };

  const toggleAll = () => {
    clearPreview();
    if (selectedCount === totalCount && totalCount > 0) {
      setAllSelected(false);
      setSelectionOverrides(new Set());
      return;
    }

    setAllSelected(true);
    setSelectionOverrides(new Set());
  };

  const resolveSelectedRecipeIds = async (): Promise<number[]> => {
    if (!allSelected) {
      return Array.from(selectionOverrides);
    }

    const allRecipeIds = await loadAllBankRecipeIds();
    return allRecipeIds.filter((id) => !selectionOverrides.has(id));
  };

  const loadSelectedGovernedRecipes = async () =>
    loadGovernedPublishingRecipes(await resolveSelectedRecipeIds());

  const handlePreviewMatrix = async () => {
    if (selectedCount === 0) return;
    setIsPreviewLoading(true);
    setError(null);
    try {
      setMatrixPreview(await loadSelectedGovernedRecipes());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('publishing.loadError', 'Unable to load the governed recipes.'));
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (selectedCount === 0) return;
    setIsGenerating(true);
    setError(null);
    try {
      const governedRecipes = matrixPreview.length
        ? matrixPreview
        : await loadSelectedGovernedRecipes();
      if (output === 'matrix') {
        saveAllergenMatrixPdf(governedRecipes, { style });
      } else {
        await saveRecipeBookPdf(governedRecipes, { style });
      }
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('publishing.error', 'Unable to generate the PDF.'));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open}
      onClose={isGenerating ? undefined : onClose}
      maxWidth={output === 'matrix' && matrixPreview.length ? 'xl' : 'md'}
      fullWidth>
      <DialogTitle>{t('recipeBank.book.title', 'Publish recipes')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Typography color="text.secondary">
            {t('publishing.bookDescription', 'Choose the governed recipes to include, the output type and the presentation style. Commercial costing is excluded from these standard publishing profiles.')}
          </Typography>

          <Box>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 1 }}>
              {t('publishing.outputType', 'Output type')}
            </Typography>
            <RadioGroup row
              value={output}
              onChange={(event) => {
                setOutput(event.target.value as 'book' | 'matrix');
                clearPreview();
              }}>
              <FormControlLabel value="book"
                control={<Radio />}
                label={t('publishing.recipeBook', 'Recipe book / selected recipe pack')} />
              <FormControlLabel value="matrix"
                control={<Radio />}
                label={t('publishing.allergenMatrix', 'Allergen & dietary matrix')} />
            </RadioGroup>
          </Box>

          <Box>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 1 }}>
              {t('publishing.presentation', 'Presentation')}
            </Typography>
            <RadioGroup
              value={style}
              onChange={(event) => setStyle(event.target.value as RecipePublishStyle)}
            >
              <FormControlLabel value="print"
                control={<Radio />}
                label={t('publishing.printFriendly', 'Print-friendly - minimal ink, no decorative imagery')} />
              <FormControlLabel value="full"
                control={<Radio />}
                label={t('publishing.fullColour', 'Full-colour - richer presentation with imagery where available')} />
            </RadioGroup>
          </Box>

          <Divider />

          <Stack direction="row"
            alignItems="center"
            justifyContent="space-between">
            <Typography variant="textSM"
              fontWeight={600}>
              {t('publishing.recipesSelected', '{{selected}} of {{total}} recipes selected', {
                selected: selectedCount,
                total: totalCount,
              })}
            </Typography>
            <Button size="md"
              variant="secondary"
              onClick={toggleAll}
              disabled={isLoading || totalCount === 0}>
              {selectedCount === totalCount && totalCount > 0
                ? t('publishing.selectNone', 'Select none')
                : t('publishing.selectAll', 'Select all')}
            </Button>
          </Stack>

          {error && <Typography color="error">{error}</Typography>}
          {isLoading && recipes.length === 0 ? (
            <Typography color="text.secondary">{t('loading')}</Typography>
          ) : (
            <>
              <Stack divider={<Divider />}
                spacing={0}>
                {recipes.map((recipe) => (
                  <FormControlLabel
                    key={recipe.id}
                    sx={{
                      alignItems: 'flex-start',
                      m: 0,
                      py: 1.5,
                      width: '100%',
                    }}
                    control={(
                      <Checkbox
                        checked={isRecipeSelected(recipe.id)}
                        onChange={() => toggleRecipe(recipe.id)}
                      />
                    )}
                    label={(
                      <Stack spacing={0.5}
                        sx={{ pt: 0.25 }}>
                        <Typography variant="textSM"
                          fontWeight={600}>
                          {recipe.description || t('publishing.untitledRecipe', 'Untitled recipe')}
                        </Typography>
                        <Typography variant="textXS"
                          color="text.secondary">
                          {recipe.itemNumber || '—'}
                        </Typography>
                        <Typography variant="textXS"
                          color="text.secondary">
                          {recipe.primaryCategory || t('publishing.noCategory', 'No category')}
                        </Typography>
                        <Typography variant="textXS"
                          color="text.secondary">
                          {recipe.currentVersion
                            ? `v${recipe.currentVersion}`
                            : t('publishing.legacy', 'legacy governed')}
                        </Typography>
                      </Stack>
                    )}
                  />
                ))}
              </Stack>

              {selectionPageCount > 1 && (
                <Box sx={{ mt: 1 }}>
                  <CustomPagination
                    page={selectionPage}
                    setPage={setSelectionPage}
                    maxPages={selectionPageCount}
                    breakpointForChangeDisplay={120000}
                    useNoDataMessage={false}
                  />
                </Box>
              )}
            </>
          )}

          {output === 'matrix' && (
            <>
              <Stack direction="row"
                justifyContent="flex-end">
                <Button
                  variant="secondary"
                  startIcon={<VisibilityOutlined />}
                  disabled={isLoading || isPreviewLoading || selectedCount === 0}
                  onClick={() => { void handlePreviewMatrix(); }}
                >
                  {isPreviewLoading
                    ? t('publishing.loadingPreview', 'Loading preview...')
                    : t('publishing.previewMatrix', 'Preview matrix')}
                </Button>
              </Stack>
              {matrixPreview.length > 0 && <GovernedMatrixPreview recipes={matrixPreview} />}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}
          disabled={isGenerating}>{t('actions.cancel', 'Cancel')}</Button>
        <Button
          variant="primary"
          startIcon={<PictureAsPdf />}
          disabled={isLoading || isGenerating || selectedCount === 0}
          onClick={() => { void handleGenerate(); }}
        >
          {isGenerating
            ? t('publishing.generating', 'Generating...')
            : t('publishing.downloadPdf', 'Download PDF')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateRecipeBookDialog;
